import React, { useEffect, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl'
import { PageTitle } from '../../../_metronic/layout/core'
import "flatpickr/dist/themes/light.css";
import { clearManufacturedProducts, getManufacturedProducts } from './productionActions';
import { getManufacturingPolicyTypes, getUser } from '../user-management/userActions';
import { getOrder, patchOrderContentStatus } from '../order/orderActions';
import { useAuth } from "../../../app/modules/auth";
import OrderComponentPrint from '../order/orderComponentPrint';
import ProductionsList from './ProductionsList';
import { enumManufacturingProductGroupType } from '../../../_metronic/helpers/enumManufacturingProductGroupType';

const Productions = (props) => {
    const intl = useIntl();
    const { currentUser } = useAuth();
    const [activeTab, setActiveTab] = useState(null);
    const [userManufacturingProductGroups, setUserManufacturingProductGroups] = useState([]);
    const [displayData, setDisplayData] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [remainingOrdersCount, setRemainingOrdersCount] = useState(0);
    const [readyOrdersCount, setReadyOrdersCount] = useState(0);
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [filterType, setFilterType] = useState('all'); // 'all', 'ready', 'remaining'

    const {
        manufacturedProducts,
        manufacturingPolicyTypes,
        user,
        loading,
    } = props;

    const {
        getManufacturedProducts,
        getManufacturingPolicyTypes,
        getUser,
        clearManufacturedProducts,
        getOrder,
        patchOrderContentStatus
    } = props;

    useEffect(() => {
        getUser(currentUser.id);
        getManufacturingPolicyTypes();
    }, []);

    useEffect(() => {
        if (user && user.manufacturingProductGroups && manufacturingPolicyTypes) {
            const filteredGroups = manufacturingPolicyTypes.filter(policy => user.manufacturingProductGroups.includes(policy.id));
            filteredGroups.sort((a, b) => {
                if (a.id === enumManufacturingProductGroupType.All) return 1;
                if (b.id === enumManufacturingProductGroupType.All) return -1;
                return 0;
            });
            setUserManufacturingProductGroups(filteredGroups);
        }
    }, [user, manufacturingPolicyTypes]);

    useEffect(() => {
        if (userManufacturingProductGroups.length > 0) {
            setActiveTab(userManufacturingProductGroups[0].id);
        }
    }, [userManufacturingProductGroups]);

    useEffect(() => {
        if (activeTab) {
            setExpandedOrder(null);
            setFilterType('all');
            clearManufacturedProducts();
            getManufacturedProducts({ manufacturingProductGroupTypes: [activeTab] });
        }
    }, [activeTab]);

    useEffect(() => {
        if (props.manufacturedProducts && props.manufacturedProducts.items) {
            const allOrders = props.manufacturedProducts.items.flatMap(item => item.manufactureProductGroupItems || []);

            setDisplayData(allOrders);

            const allContents = allOrders.flatMap(order => order.orderContents || []);
            setTotalItems(allContents.length);

            const readyOrders = allOrders.filter(order => order.orderReady || (order.orderContents && order.orderContents.every(c => c.checked))).length;
            const remainingOrders = allOrders.length - readyOrders;

            setReadyOrdersCount(readyOrders);
            setRemainingOrdersCount(remainingOrders);
        } else {
            setDisplayData([]);
            setTotalItems(0);
            setReadyOrdersCount(0);
            setRemainingOrdersCount(0);
        }
    }, [props.manufacturedProducts]);

    const productionsBreadcrumbs = [
        {
            title: intl.formatMessage({ id: 'PRODUCTIONS.BREADCRUMB.TITLE' }),
            path: '/production-management/productions',
            isSeparator: false,
            isActive: false,
        },
        {
            isSeparator: true,
            isActive: false,
        }
    ]

    const renderContentImageLink = (content) => {
        const imageSrc = content?.imageLink ? content.imageLink : '/media/stock/ecommerce/default-product.png';
        return (
            <div className="symbol symbol-circle symbol-50px me-3">
                <div className="symbol-label">
                    <img src={imageSrc} alt={content.productName} className="w-100" />
                </div>
            </div>
        )
    }

    const renderContentItemName = (content) => {
        if (content && content.productId > 0) {
            return (
                <div className="d-flex align-items-center">
                    <a target='_blank' href={`/catalog-management/products/${content.productId}/detail`}>
                        {renderContentImageLink(content)}
                    </a>
                    <div className="d-flex flex-column">
                        <a target='_blank' href={`/catalog-management/products/${content.productId}/detail`} className="text-dark text-hover-primary mb-1 fw-bold">{content.productName} - <span className='fw-light'>{content.productCode}</span></a>
                        <span className='fw-bold'>{content.variantName} - <span className='fw-light'>{content.variantCode}</span></span>
                    </div>
                </div>
            )
        }
        return (
            <>
                {content.name}
            </>
        )
    }

    const onContentCheckChange = (item) => {
        setIsUpdating(true);
        const updatedData = displayData.map(order => {
            if (order.orderId === item.orderId) {
                const updatedContents = order.orderContents.map(content => {
                    if (content.id === item.id) {
                        return { ...content, checked: !content.checked };
                    }
                    return content;
                });
                const allItemsReady = updatedContents.every(content => content.checked);
                return { ...order, orderContents: updatedContents, orderReady: allItemsReady };
            }
            return order;
        });
        setDisplayData(updatedData);

        const readyOrders = updatedData.filter(order => order.orderReady).length;
        const remainingOrders = updatedData.length - readyOrders;

        setReadyOrdersCount(readyOrders);
        setRemainingOrdersCount(remainingOrders);

        patchOrderContentStatus(item.orderId, item.id).then(() => {
            setIsUpdating(false);
        });
    }

    const onPrintContent = async (content) => {
        const newTab = window.open('', '_blank');
        if (!newTab) {
            console.error('Popup blocked');
            return;
        }

        const response = await getOrder(content.orderId);
        if (response && response.data) {
            const markup = renderToStaticMarkup(
                <OrderComponentPrint
                    order={response.data}
                    content={content}
                    intl={intl}
                />
            );

            newTab.document.write(markup);
            newTab.document.close();
            newTab.print();
        } else {
            newTab.close();
        }
    }

    const handleFilterClick = (type) => {
        if (filterType === type) {
            setFilterType('all');
        } else {
            setFilterType(type);
        }
    }

    const filteredDisplayData = displayData.filter(order => {
        if (filterType === 'all') return true;
        if (filterType === 'ready') return order.orderReady;
        if (filterType === 'remaining') return !order.orderReady;
        return true;
    });

    return (
        <>
            <PageTitle breadcrumbs={productionsBreadcrumbs}>{intl.formatMessage({ id: 'PRODUCTIONS.BREADCRUMB.TITLE' })}</PageTitle>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card card-flush">
                        <div className="card-header align-items-center py-5 gap-2 gap-md-5">
                            <div className='btn-group w-100 flex-wrap mb-5' role='group'>
                                {userManufacturingProductGroups.map((group, index) => (
                                    <button
                                        key={index}
                                        type='button'
                                        className={`btn ${activeTab === group.id ? 'btn-primary' : 'btn-secondary'}`}
                                        onClick={() => setActiveTab(group.id)}
                                        title={group.name}
                                    >
                                        {group.id === enumManufacturingProductGroupType.All ? (
                                            <i className="ki-duotone ki-element-11 fs-2">
                                                <span className="path1"></span>
                                                <span className="path2"></span>
                                                <span className="path3"></span>
                                                <span className="path4"></span>
                                            </i>
                                        ) : (
                                            group.name
                                        )}
                                    </button>
                                ))}
                            </div>
                            <div className='d-flex justify-content-end'>
                                <div
                                    className={`card me-5 cursor-pointer ${filterType === 'ready' ? 'bg-light-success border-success border' : ''}`}
                                    onClick={() => handleFilterClick('ready')}
                                >
                                    <div className='card-body p-3'>
                                        <p className='fw-bold fs-5 mb-0'>
                                            <i className="ki-duotone ki-check-circle fs-2 text-success me-2">
                                                <span className="path1"></span>
                                                <span className="path2"></span>
                                            </i>
                                            {readyOrdersCount}
                                        </p>
                                    </div>
                                </div>
                                <div
                                    className={`card cursor-pointer ${filterType === 'remaining' ? 'bg-light-warning border-warning border' : ''}`}
                                    onClick={() => handleFilterClick('remaining')}
                                >
                                    <div className='card-body p-3'>
                                        <p className='fw-bold fs-5 mb-0'>
                                            <i className="ki-duotone ki-time fs-2 text-warning me-2">
                                                <span className="path1"></span>
                                                <span className="path2"></span>
                                            </i>
                                            {remainingOrdersCount}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <div className='tab-content'>
                                <div className={`tab-pane fade show active`} role="tab-panel">
                                    <ProductionsList
                                        displayData={filteredDisplayData}
                                        loading={loading}
                                        expandedOrder={expandedOrder}
                                        setExpandedOrder={setExpandedOrder}
                                        onContentCheckChange={onContentCheckChange}
                                        onPrintContent={onPrintContent}
                                        intl={intl}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >
        </>
    );
};

const mapStateToProps = state => {
    return {
        manufacturedProducts: state.Production.manufacturedProducts,
        manufacturingPolicyTypes: state.User.manufacturingPolicyTypes,
        user: state.User.user,
        loading: state.Production.loading,
    };
};

const mapActionsToProps = {
    getManufacturedProducts,
    getManufacturingPolicyTypes,
    getUser,
    clearManufacturedProducts,
    getOrder,
    patchOrderContentStatus
}

export default connect(mapStateToProps, mapActionsToProps)(Productions)
